import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Star, Send, CheckCircle2, MessageCircle } from "lucide-react";

const STAR_FILL = "#f59e0b";

/**
 * Participant Portal · Home dashboard — Feedback card.
 *
 * Only renders when the partner has POSTed
 * /partner-portal/feedback/{booking_id}/request for this participant.
 *
 * Flow:
 *  - 1-3★ : reveals the comment textarea + Submit; on submit the row lands
 *           in the partner's Feedback tab as "Feedback submitted".
 *  - 4-5★ : submits immediately, then window.open()s the partner's public
 *           review URL in a new tab.  The card converts to a Thank-you
 *           state that says "Rated on Trust Pilot / Google".
 *  - Duplicate / already-submitted: renders the Thank-you state.
 */
export default function ParticipantFeedbackCard() {
  const [state, setState] = useState(null); // { pending, submitted, partner_name, ... }
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [redirected, setRedirected] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/participant/feedback-request");
      setState(data);
    } catch {
      setState({ pending: false, submitted: false });
    }
  };
  useEffect(() => { load(); }, []);

  if (!state) return null;
  if (!state.pending && !state.submitted) return null; // nothing to prompt

  // Already-submitted state
  if (state.submitted) {
    const stars = state.rating || 0;
    return (
      <div className="aii-card p-5 border-l-4 border-emerald-500" data-testid="pfb-submitted-card">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={28} className="text-emerald-500 flex-shrink-0"/>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Feedback shared</div>
            <div className="font-display font-semibold mt-0.5">Thank you for rating {state.partner_name}!</div>
            <div className="mt-1 flex items-center gap-1">
              {[1,2,3,4,5].map(s => (
                <Star key={s} size={14}
                      className={s <= stars ? "text-amber-500" : "text-slate-300"}
                      fill={s <= stars ? STAR_FILL : "none"} strokeWidth={1.5}/>
              ))}
              <span className="ml-1.5 text-xs font-semibold text-[color:var(--aii-text-2)]">{stars}/5</span>
            </div>
            {redirected && (
              <div className="mt-1 text-[11px] text-emerald-700">
                We&apos;ve opened the public review page in a new tab — thanks for taking a moment to share it there too!
              </div>
            )}
            {state.comment && (
              <div className="mt-2 text-[11px] text-[color:var(--aii-text-2)] bg-[color:var(--aii-bg)] border border-[color:var(--aii-border)] rounded p-2 flex items-start gap-1.5">
                <MessageCircle size={11} className="mt-0.5 flex-shrink-0 text-[color:var(--aii-muted)]"/>
                <span className="whitespace-pre-wrap">{state.comment}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Pending state — show the rating widget
  const submit = async (finalRating) => {
    setBusy(true);
    try {
      const { data } = await api.post("/participant/feedback-request/submit", {
        rating: finalRating,
        comment: finalRating <= 3 ? comment : "",
      });
      if (data.redirect_url) {
        // Open the public review site in a NEW window per the founder's spec —
        // never hijack the participant's tab.
        window.open(data.redirect_url, "_blank", "noopener,noreferrer");
        setRedirected(true);
      }
      toast.success(finalRating >= 4 ? "Thanks! Please leave a public review too" : "Feedback shared with your partner");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
    setBusy(false);
  };

  const onStarClick = (star) => {
    setRating(star);
    if (star >= 4) submit(star);
  };

  const showComment = rating > 0 && rating <= 3;
  return (
    <div className="aii-card p-5 border-l-4 border-amber-400" data-testid="pfb-pending-card">
      <div className="flex items-start gap-3 flex-wrap">
        <Star size={28} className="text-amber-500 flex-shrink-0" fill={STAR_FILL} strokeWidth={1.5}/>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Feedback requested</div>
          <div className="font-display font-semibold mt-0.5">
            {state.partner_name} would love your feedback
          </div>
          <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
            How was your experience? We would love to hear from you to make our programs more sustainable and exciting for others.
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center gap-1 mt-4 mb-2" data-testid="pfb-stars">
        {[1,2,3,4,5].map(s => {
          const active = s <= (hover || rating);
          return (
            <button
              key={s}
              type="button"
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(0)}
              onClick={() => onStarClick(s)}
              disabled={busy}
              data-testid={`pfb-star-${s}`}
              className="p-1 hover:scale-110 transition disabled:opacity-50"
              aria-label={`Rate ${s} star${s === 1 ? "" : "s"}`}
            >
              <Star size={36} className={active ? "text-amber-500" : "text-slate-300"}
                    fill={active ? STAR_FILL : "none"} strokeWidth={1.5}/>
            </button>
          );
        })}
      </div>
      {showComment && (
        <div className="mt-3 space-y-2" data-testid="pfb-comment-block">
          <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">
            Suggestions &amp; feedback
          </label>
          <textarea
            rows={3}
            className="input-aii w-full"
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="Tell your partner what could have been better…"
            data-testid="pfb-comment"
          />
          <button
            onClick={() => submit(rating)}
            disabled={busy}
            data-testid="pfb-submit"
            className="w-full px-4 py-2.5 bg-brand text-white rounded font-semibold hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Send size={13}/> {busy ? "Submitting…" : "Submit feedback"}
          </button>
          <div className="text-[10px] text-[color:var(--aii-muted)] text-center">
            Sent privately to {state.partner_name}.
          </div>
        </div>
      )}
      {rating >= 4 && busy && (
        <div className="mt-3 text-center text-xs text-[color:var(--aii-muted)] italic">Submitting your rating…</div>
      )}
    </div>
  );
}
